/**
 * The domain root (`/`).
 *
 * Three-way branch, and the ORDER of the branches is the fix for the marketing
 * flash a signed-in user used to see on `fias.io`. Background: the CloudFront
 * rewrite that serves prerendered site-arche snapshots is unconditional by
 * design (CLAUDE.md § Site Arches), so everybody — signed in or not — is served
 * the same cached marketing HTML at `/` and the app boots over the top of it.
 *
 * Lives in its own module rather than inline in `App.tsx` so the branching can
 * be unit-tested without pulling the entire route table's import graph in.
 */
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import { BootSplash } from '@/components/BootSplash';
import { hasBootSessionHint } from '@/lib/boot-session-hint';
import { hasSessionHint } from '@/lib/auth-refresh';
import { FiasSiteArchePage } from '@/arche/arches/fias-site';
import { IS_COMPANY_PORTAL } from '@/lib/portal-host';
import { PortalHome } from '@/auth/company/PortalPages';

export function Home() {
  // A Company portal's front door is its own (ADR-024 §4.7). Never true in the
  // prerender, so the snapshot of `/` is unchanged.
  if (IS_COMPANY_PORTAL) return <PortalHome />;
  return <PlatformHome />;
}

function PlatformHome() {
  const { isAuthenticated, isLoading, isDevMode } = useAuth();
  const [returningVisitor] = React.useState(() => hasBootSessionHint() || hasSessionHint());

  // Redirect authenticated users to the main arche interface. The splash rides
  // along because <Navigate> renders null and only moves the router in an
  // effect — without it this commit paints an empty #root between the snapshot
  // splash being torn down and the destination route mounting.
  if (isAuthenticated) {
    return (
      <>
        <BootSplash />
        <Navigate to="/a/arche_make_a_request" replace />
      </>
    );
  }

  // The loading branch is conditional on the visitor looking like a RETURNING
  // one, and that condition is the whole design.
  //
  // An unconditional skeleton would replace the prerendered snapshot with a
  // spinner and then swap back — a visible flash on the busiest page on the
  // site, imposed on every anonymous visitor and every crawler to spare the
  // minority who are signed in. So anonymous visitors keep falling straight
  // through to the marketing site below, exactly as before, and only a browser
  // that has actually held a session waits on the indicator instead of being
  // shown marketing it is about to be redirected away from.
  //
  // Two signals, because they cover different deployments. `hasBootSessionHint`
  // reads the attribute the snapshot's inline script set before first paint —
  // the production path, where the marketing HTML is already on screen and this
  // render has to agree with the splash covering it. `hasSessionHint` reads the
  // same localStorage key directly, which is what dev and any non-snapshot
  // route have (there is no inline script there to set the attribute).
  //
  // Snapshotted at mount rather than read per render: `getCurrentUser` clears
  // the localStorage key when it settles on logged-out, and re-reading it live
  // would flip this branch mid-boot for reasons unrelated to what is on screen.
  if (isLoading && returningVisitor) {
    return <BootSplash />;
  }

  // Signed-out visitors get the public marketing site at the domain root.
  // Crawlers are always signed out, so this is the branch they see — and the
  // one the build-time snapshot prerenders. The site is reachable at its
  // canonical /a/fias-site URL too, including for signed-in users.
  return (
    <>
      <FiasSiteArchePage basePath="/" />

      {/* Local-dev shortcut to the Guest Trial Mode entry point. The product
          entry is the Get Started arche (/a/arche_get_started), reached from an
          ad's landing page; in dev it offers the trial without an ad click (the
          server skips that check locally). Kept outside the marketing
          components deliberately: that surface must stay free of auth state so
          it can be rendered to static HTML at build time. */}
      {isDevMode && (
        <div className="fixed bottom-4 left-4 z-50">
          <Link
            to="/a/arche_get_started"
            className="inline-flex items-center rounded-md border bg-card px-4 h-10 text-sm font-medium text-muted-foreground shadow-sm transition-colors hover:text-foreground"
          >
            Dev: try Fias without an account
          </Link>
        </div>
      )}
    </>
  );
}
